import { H4 } from "@/components/ui/typography";
import { ContainerPrimary } from "../ui";
import { useNavigate, useParams } from "react-router-dom";
import { MISSING_ID_IN_ROUTE } from "@/utils/constant";
import { useGetDataPDF, useMediaMapCreate } from "@/api/generate-hpdb";
import { DefaultButton, DownloadButton } from "@/components/ui/button";
import { LucideEye } from "lucide-react";
import { swalError, swalLoading, swalSuccess } from "@/utils/lib/swal";
import { useRFS } from "@/api/rfs";
import { useRef, useState } from "react";
import { MapPrint } from "@/pages/map/MapPrint";
import { AxiosError } from "axios";
import { z } from "zod";
import { queryClient } from "@/utils/lib/queryClient";

interface InputProps {
  media?: {
    target: string;
    before?: (files: FileList) => File | Promise<File> | undefined;
  };
  disabled?: boolean;
}

export function GeneratePDFHandler() {
  const { id } = useParams();
  if (!id) {
    throw new Error(MISSING_ID_IN_ROUTE);
  }

  const navigate = useNavigate();
  const pageRef = useRef<HTMLDivElement>(null);

  const { data: rfs } = useRFS({ id });
  const { data: dataPDF } = useGetDataPDF({
    rfsId: id,
  });

  const memoNotFound = dataPDF;

  function handleDownload() {
    if (pageRef.current) {
      const printArea = document.createElement("div");
      printArea.id = "print-area";
      printArea.style.position = "absolute";
      printArea.style.top = "0";
      printArea.style.left = "0";
      printArea.style.width = "100%";
      printArea.style.height = "100%";
      printArea.style.zIndex = "-10";

      printArea.innerHTML = pageRef.current.innerHTML;
      document.body.appendChild(printArea);

      window.print();

      requestAnimationFrame(() => {
        document.body.removeChild(printArea);
      });
    }
  }

  return (
    <ContainerPrimary>
      <div className="flex-grow whitespace-nowrap">
        <H4>Convert .kmz to .pdf</H4>
      </div>
      <div className="flex items-center gap-x-2">
        <InputImage
          disabled={!memoNotFound}
          media={{
            target: "maps",
          }}
        />
        <DefaultButton
          disabled={!memoNotFound || !rfs?.documentMaps}
          mood="axiata-blue-outline"
          onClick={() => {
            navigate(`/map/${id}`);
          }}
        >
          <LucideEye size={20} />
        </DefaultButton>
        <DownloadButton
          onClick={handleDownload}
          className="ml-auto"
          disabled={!memoNotFound || !rfs?.documentMaps}
        />

        {/* PAGE RENDER IN PDF */}
        <div ref={pageRef} style={{ display: "none" }}>
          {dataPDF && <MapPrint dataPDF={dataPDF} />}
        </div>
      </div>
    </ContainerPrimary>
  );
}

const imageSchema = z
  .instanceof(File)
  .optional()
  .refine(
    (file) =>
      file?.type === "image/png" ||
      file?.type === "image/jpeg" ||
      file?.type === "image/jpg",
    {
      message: "Please upload files in .png, jpg and jpeg format only",
    },
  );

function InputImage({ media, disabled }: InputProps) {
  const { id } = useParams();
  if (!id) {
    throw new Error(MISSING_ID_IN_ROUTE);
  }

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { mutate } = useMediaMapCreate();
  const { data: dataPDF } = useGetDataPDF({
    rfsId: id,
  });

  async function handleAutoMedia(e: React.ChangeEvent<HTMLInputElement>) {
    if (!id) {
      throw new Error(MISSING_ID_IN_ROUTE);
    }

    if (!media || !e.target.files?.length) {
      return;
    }
    const beforeResult = await media.before?.(e.target.files);
    if (media.before && !beforeResult) {
      return;
    }

    const file = e.target.files[0];
    setSelectedFile(file);

    const validationResult = imageSchema.safeParse(file);
    if (!validationResult.success) {
      swalError.fire({
        title: "Invalid File",
        text: validationResult.error.errors[0].message,
      });
      return;
    }

    swalLoading.fire({
      title: "Uploading Media",
      text: "Please wait, upload process is in progress",
    });

    mutate(
      {
        id,
        data: {
          file: media.before && beforeResult ? beforeResult : e.target.files[0],
          target: media.target,
        },
      },
      {
        onSuccess: (response) => {
          swalSuccess.fire({
            text: "Upload success",
          });

          if (response.originalFileName) {
            setSelectedFile(null);
          }

          queryClient.invalidateQueries({ queryKey: ["rfss"] });
          queryClient.invalidateQueries({ queryKey: ["kmzpdf"] });
        },
        onError: (result: Error) => {
          swalLoading.close();
          const err = result as AxiosError;
          const errorMessage = (
            err.response?.data as {
              data: unknown;
            }
          )?.data;

          swalError.fire({
            title: "Media Upload failed",
            text: `${errorMessage}`,
          });
        },
      },
    );
  }

  const getFileName = (path: string | null | undefined) => {
    if (!path) return "No file chosen";

    if (dataPDF?.fileName) {
      return dataPDF?.fileName;
    }

    const parts = path.split("/");
    return parts[parts.length - 1];
  };

  const documentName = getFileName(dataPDF?.documentMaps);

  return (
    <div className="mr-3">
      <div className="flex items-center disabled:bg-slate-400">
        <input
          disabled={disabled}
          name="mapMedia"
          type="file"
          className="hidden"
          id="fileInput"
          onChange={handleAutoMedia}
          accept=".jpg, .jpeg, .png"
        />
        <label
          htmlFor="fileInput"
          className={`rounded ${
            disabled
              ? "cursor-not-allowed bg-gray-300"
              : "cursor-pointer bg-neutral-700"
          }  px-3 py-1 text-white`}
        >
          Choose File
        </label>
        <span className="ml-2">
          {selectedFile ? selectedFile.name : documentName}
        </span>
      </div>
    </div>
  );
}
